'use client';

import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthLayout from '../components/AuthLayout.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import Turnstile from '../components/auth/Turnstile';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../components/ui/form.tsx';
import { Input } from '../components/ui/input.tsx';
import useAuth from '../hooks/useAuth.js';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string().min(6, {
    message: 'Password must be at least 6 characters.',
  }),
  turnstileToken: z.string({
    required_error: 'Please complete the security check',
  }),
});

export default function Login() {
  const navigate = useNavigate();
  const { login, loginLoading } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isRegisterLoading, setIsRegisterLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileError, setTurnstileError] = useState('');

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      turnstileToken: '',
    },
  });

  const onTurnstileVerify = (token) => {
    setTurnstileError('');
    setTurnstileToken(token);
    form.setValue('turnstileToken', token);
  };

  const onSubmit = async (values) => {
    if (!turnstileToken) {
      setTurnstileError('Please complete the security check');
      return;
    }

    if (loginLoading) return;

    try {
      await login(values);
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.message || 'Failed to login. Please try again.');
      form.setError('root', {
        type: 'manual',
        message: error.message || 'Failed to login. Please try again.',
      });
    }
  };

  const handleRegister = () => {
    setIsRegisterLoading(true);
    navigate('/signup');
  };

  return (
    <AuthLayout
      title="Welcome back"
      subtitle="Please enter your details to sign in"
      wave
    >
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.formState.errors.root && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-md">
              {form.formState.errors.root.message}
            </div>
          )}

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    placeholder="example@mail.com"
                    {...field}
                    disabled={loginLoading}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      {...field}
                      className="pr-10"
                      disabled={loginLoading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      disabled={loginLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-500" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-500" />
                      )}
                    </button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loginLoading}
              />
              <label
                htmlFor="remember-me"
                className="ml-2 block text-sm text-gray-900"
              >
                Remember me
              </label>
            </div>

            <div className="text-sm">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="font-medium text-purple-600 hover:text-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loginLoading}
              >
                Forgot your password?
              </button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-center flex-col items-center gap-2">
              {process.env.REACT_APP_NODE_ENV === 'production' ||
              process.env.REACT_APP_NODE_ENV === 'development' ? (
                <>
                  <Turnstile
                    sitekey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
                    onVerify={onTurnstileVerify}
                  />
                  {turnstileError && (
                    <p className="text-sm text-red-600">{turnstileError}</p>
                  )}
                </>
              ) : null}
            </div>

            {process.env.REACT_APP_NODE_ENV === 'production' ||
            process.env.REACT_APP_NODE_ENV === 'development' ? (
              <button
                type="submit"
                disabled={loginLoading || !turnstileToken}
                className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loginLoading ? (
                  <div className="flex items-center justify-center w-full">
                    <LoadingSpinner size="sm" className="mr-2" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={loginLoading}
                className="w-full h-10 inline-flex items-center justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loginLoading ? (
                  <div className="flex items-center justify-center w-full">
                    <LoadingSpinner size="sm" className="mr-2" />
                    <span>Signing in...</span>
                  </div>
                ) : (
                  'Sign in'
                )}
              </button>
            )}

            <p className="text-center text-sm text-gray-600">
              Don't have an account?{' '}
              <button
                type="button"
                onClick={handleRegister}
                disabled={isRegisterLoading || loginLoading}
                className="font-medium text-purple-600 hover:text-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRegisterLoading ? 'Redirecting...' : 'Sign up'}
              </button>
            </p>
          </div>
        </form>
      </Form>
    </AuthLayout>
  );
}
